import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ResponsiveDialog, ResponsiveDialogContent, ResponsiveDialogHeader, ResponsiveDialogTitle, ResponsiveDialogTrigger } from '@/components/ui/responsive-dialog';
import PasswordStrengthBar from 'react-password-strength-bar';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '@/services/api-client';
import { toast } from 'sonner';
import { handleApiError } from '@/lib/handle-api-error';
import { PasswordInput } from '@/components/ui/password-input';
import { PhoneInput } from '@/components/ui/phone-input';
import { FormError } from '@/components/ui/form-error';

const signupSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  email: z.string().email("Email inválido"),
  phone: z.string().optional(),
  monitored_processes_status: z.string().optional(),
  password: z.string().min(8, "A senha deve ter no mínimo 8 caracteres"),
  re_password: z.string(),
  terms: z.boolean().refine((val) => val === true, {
    message: "Você deve aceitar os termos e condições",
  }),
}).refine((data) => data.password === data.re_password, {
  message: "As senhas não coincidem",
  path: ["re_password"],
});

type SignupFormData = z.infer<typeof signupSchema>;

export const SignUpForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState('');

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      terms: false,
    },
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = form;

  const handleSignup = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await apiClient.signup(data);
      toast.success("Conta criada com sucesso!");
      navigate('/login');
    } catch (error) {
      handleApiError(error, { form });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl">
      <CardHeader>
        <CardTitle>Crie sua conta</CardTitle>
        <CardDescription>Digite seus dados para criar a conta</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleSignup)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Nome Completo</Label>
              <Input id="name" placeholder="Digite seu nome completo" {...register("name")} />
              <FormError message={errors.name?.message} />
            </div>
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" placeholder="Digite seu e-mail" {...register("email")} />
              <FormError message={errors.email?.message} />
            </div>
            <div>
              <Label htmlFor="phone">Telefone</Label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    ref={field.ref}
                    value={field.value}
                    onAccept={(value) => field.onChange(value)}
                    onBlur={field.onBlur}
                  />
                )}
              />
              <FormError message={errors.phone?.message} />
            </div>
            <div>
              <Label htmlFor="processes">Número de processos a acompanhar</Label>
              <Select onValueChange={(value) => setValue("monitored_processes_status", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lte_40">Até 40 processos</SelectItem>
                  <SelectItem value="between_41_to_150">De 41 a 150 processos</SelectItem>
                  <SelectItem value="between_151_to_500">De 151 a 500 processos</SelectItem>
                  <SelectItem value="between_501_to_1000">De 501 a 1000 processos</SelectItem>
                  <SelectItem value="gte_1001">Mais de 1000 processos</SelectItem>
                  <SelectItem value="only_consulting">Atuo apenas no consultivo</SelectItem>
                </SelectContent>
              </Select>
              <FormError message={errors.monitored_processes_status?.message} />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <PasswordInput
                id="password"
                placeholder="Digite sua senha"
                {...register("password")}
                onChange={(e) => {
                  setPassword(e.target.value);
                  register("password").onChange(e);
                }}
              />
              <PasswordStrengthBar
                password={password}
                shortScoreWord="muito curta"
                scoreWords={['muito fraca', 'fraca', 'razoável', 'boa', 'forte']}
              />
              <FormError message={errors.password?.message} />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirmar Senha</Label>
              <PasswordInput
                id="confirm-password"
                placeholder="Confirme sua senha"
                {...register("re_password")}
              />
              <FormError message={errors.re_password?.message} />
            </div>
          </div>
          <div className="flex items-center space-x-2 mt-4">
            <Controller
              name="terms"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="terms"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              <label htmlFor="terms" className="cursor-pointer">
                Estou de acordo com os{" "}
              </label>
              <ResponsiveDialog>
                <ResponsiveDialogTrigger asChild>
                  <span className="text-primary cursor-pointer hover:underline">Termos e Condições</span>
                </ResponsiveDialogTrigger>
                <ResponsiveDialogContent>
                  <ResponsiveDialogHeader>
                    <ResponsiveDialogTitle>Termos e Condições</ResponsiveDialogTitle>
                  </ResponsiveDialogHeader>
                  <p>Conteúdo dos termos e condições...</p>
                </ResponsiveDialogContent>
              </ResponsiveDialog>
              {" e "}
              <ResponsiveDialog>
                <ResponsiveDialogTrigger asChild>
                  <span className="text-primary cursor-pointer hover:underline">Políticas de Privacidade</span>
                </ResponsiveDialogTrigger>
                <ResponsiveDialogContent>
                  <ResponsiveDialogHeader>
                    <ResponsiveDialogTitle>Políticas de Privacidade</ResponsiveDialogTitle>
                  </ResponsiveDialogHeader>
                  <p>Conteúdo da política de privacidade...</p>
                </ResponsiveDialogContent>
              </ResponsiveDialog>
            </div>
          </div>
          <FormError message={errors.terms?.message} />
          <FormError message={errors.root?.serverError?.message} />

          <Button className="w-full mt-4" type="submit" disabled={isLoading}>
            {isLoading ? "Criando conta..." : "Criar conta"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        <p className="text-sm text-center">
          Já tem uma conta?{" "}
          <Link to="/login" className="text-primary">
            Faça o login
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
};
